import { computed, defineComponent, ref, watch } from 'vue';
import { useWindowingStore } from '@/src/store/view-configs/windowing';
import { Maybe } from '@/src/types';
import type { Vector2 } from '@kitware/vtk.js/types';
import { MaybeRef, unref } from 'vue';
import { useImageStatsStore } from '@/src/store/image-stats';

export function useWindowingConfig(
  viewID: MaybeRef<string>,
  imageID: MaybeRef<Maybe<string>>
) {
  const store = useWindowingStore();
  const imageStatsStore = useImageStatsStore();

  const config = computed(() => {
    const imageIdVal = unref(imageID);
    if (!imageIdVal) return undefined;
    const viewIdVal = unref(viewID);
    if (!viewIdVal) return undefined;
    return store.getConfig(viewIdVal, imageIdVal);
  });

  const width = computed({
    get: () => {
      return config.value?.width ?? 400;
    },
    set: (val) => {
      const imageIdVal = unref(imageID);
      if (!imageIdVal || val == null) return;
      store.updateConfig(unref(viewID), imageIdVal, { width: val }, true);
    },
  });

  const level = computed({
    get: () => {
      return config.value?.level ?? 40;
    },
    set: (val) => {
      const imageIdVal = unref(imageID);
      if (!imageIdVal || val == null) return;
      store.updateConfig(unref(viewID), imageIdVal, { level: val }, true);
    },
  });

  const range = computed((): Vector2 => {
    const imageIdVal = unref(imageID);
    if (!imageIdVal) return [0, 1];
    const stats = imageStatsStore.stats[imageIdVal];
    if (!stats || stats.scalarMin == null || stats.scalarMax == null)
      return [0, 1];
    return [stats.scalarMin, stats.scalarMax];
  });

  const setWindowLevel = (width: number, level: number) => {
    console.log('🎛️ useWindowingConfig.setWindowLevel():', {
      width,
      level,
      viewId: unref(viewID),
      imageId: unref(imageID),
    });

    const imageIdVal = unref(imageID);
    const viewIdVal = unref(viewID);

    if (!imageIdVal) {
      console.warn('⚠️ Cannot set window/level: no image ID');
      return;
    }

    if (!viewIdVal) {
      console.warn('⚠️ Cannot set window/level: no view ID');
      return;
    }

    if (!Number.isFinite(width) || !Number.isFinite(level)) {
      console.warn('⚠️ Cannot set window/level: invalid numbers', { width, level });
      return;
    }

    try {
      store.updateConfig(
        viewIdVal,
        imageIdVal,
        {
          width: Math.round(width),
          level: Math.round(level),
        },
        true
      );

      console.log('✅ Window/level updated successfully');
    } catch (error) {
      console.error('❌ Error updating window/level:', error);
    }
  };

  const getDebugInfo = () => {
    return {
      config: config.value,
      currentWidth: width.value,
      currentLevel: level.value,
      imageID: unref(imageID),
      viewID: unref(viewID),
      range: range.value,
    };
  };

  return {
    config,
    width,
    level,
    range,
    setWindowLevel,
    getDebugInfo,
  };
}
